import { useCallback, useMemo, useState } from "react";
import type { FormProduct } from "@shared/types";
import { useTranslation } from "react-i18next";
import { Badge } from "@bcc/components/ui/badge";
import { ChevronRight, Edit, Trash2 } from "lucide-react";
import { cn } from "@bcc/lib/utils";
import { getFormRowTestIds, getVariantAttributes } from "./product-select-form-row.utils";
import { VariantStatusBadges } from "./variant-status-badges";
import { resolveProductImage } from "@bcc/components/shopify-import/utils/get-product-image-url";
import { getFormProductKey } from "@bcc/components/shopify-import/get-form-product-key";
import { DeleteDraftDialog } from "../delete-draft-dialog";

export interface ProductSelectFormRowProps {
  product: FormProduct;
  isSelected: boolean;
  onSelect: (product: FormProduct, checked: boolean) => void;
  isSingleBagSizeEnabled?: boolean;
  onDeleteDraft?: (product: FormProduct) => Promise<void> | void;
}

export function ProductSelectFormRow({
  product,
  isSelected,
  onSelect,
  isSingleBagSizeEnabled = false,
  onDeleteDraft,
}: Readonly<ProductSelectFormRowProps>) {
  const { t } = useTranslation();
  const {
    checkboxTestId,
    titleTestId,
    noVariantsDetectedTestId,
    hasDraftTestId,
    deleteDraftTestId,
    totalVariantsTextTestId,
    totalVariantsCountTestId,
    importedLiveTestId,
    importedPendingTestId,
    importedRejectedTestId,
    allVariantsImportedTestId,
  } = useMemo(() => getFormRowTestIds(getFormProductKey(product)), [product]);

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  const { variantsCountObject, isProductEnabled, hasAllVariantsImported } = useMemo(
    () => getVariantAttributes(product, isSingleBagSizeEnabled),
    [product, isSingleBagSizeEnabled]
  );

  const hasDraft = product?.hasDraft ?? false;
  const showDraftBadge = hasDraft && variantsCountObject.localVariants === 0;
  // Independent of the badge: a product can have a saved variant draft
  // (hasVariantsDraft) while also having local variants already imported,
  // in which case the badge is hidden but the draft is still deletable.
  const canDeleteDraft = hasDraft && !!onDeleteDraft;

  const handleClick = useCallback(() => {
    onSelect(product, !isSelected);
  }, [onSelect, product, isSelected]);

  const handleDeleteDraftClick = useCallback(() => {
    setIsConfirmingDelete(true);
  }, []);

  const handleConfirmDeleteDraft = useCallback(async () => {
    setIsDeletingDraft(true);
    try {
      await onDeleteDraft?.(product);
    } finally {
      setIsDeletingDraft(false);
      setIsConfirmingDelete(false);
    }
  }, [onDeleteDraft, product]);

  const handleDeleteDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isDeletingDraft) {
        setIsConfirmingDelete(false);
      }
    },
    [isDeletingDraft]
  );

  return (
    <li className="flex items-start gap-2">
      <button
        type="button"
        aria-pressed={isSelected}
        data-testid={checkboxTestId}
        onClick={handleClick}
        disabled={!isProductEnabled}
        className={cn(
          "flex flex-1 items-start gap-4 rounded-lg border p-4 text-left transition-colors",
          isSelected && "bg-accent/50 border-primary",
          isProductEnabled ? "hover:bg-muted/50 cursor-pointer" : "opacity-50 pointer-events-none"
        )}
      >
        <img
          src={resolveProductImage(product.images, product.imageUploads)}
          alt={product.title}
          style={{ width: "80px" }}
          className="rounded-md object-cover"
        />
        <div className="flex-1 space-y-2">
          <div>
            {/* Title and Draft Badge */}
            <div className="flex items-center gap-2">
              <h3 data-testid={titleTestId} className="font-semibold">
                {product.title}
              </h3>
              {showDraftBadge && (
                <Badge
                  data-testid={hasDraftTestId}
                  className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800 shrink-0"
                >
                  <Edit className="h-3 w-3" />
                  {t("components.ProductSelectModal.draftAvailable")}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
          </div>

          {/* Variant Information */}
          <VariantStatusBadges
            variantsCountObject={variantsCountObject}
            hasAllVariantsImported={hasAllVariantsImported}
            testIds={{
              totalVariantsText: totalVariantsTextTestId,
              totalVariantsCount: totalVariantsCountTestId,
              noVariantsDetected: noVariantsDetectedTestId,
              allVariantsImported: allVariantsImportedTestId,
              importedLive: importedLiveTestId,
              importedPending: importedPendingTestId,
              importedRejected: importedRejectedTestId,
            }}
          />

          {/* Tags (if available) */}
          {product.productType && (
            <div className="flex flex-wrap gap-1">
              <Badge variant="outline" className="text-xs">
                {product.productType}
              </Badge>
            </div>
          )}
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
      </button>
      {canDeleteDraft && (
        // Sibling of the row-selection button (not nested inside it): keeps
        // delete actionable via keyboard/screen readers and independent of
        // the row button's disabled state.
        <button
          type="button"
          data-testid={deleteDraftTestId}
          onClick={handleDeleteDraftClick}
          className="mt-4 inline-flex shrink-0 items-center gap-1 self-start rounded-full border border-destructive/30 px-2 py-0.5 text-xs font-medium text-destructive hover:bg-destructive/10 cursor-pointer"
        >
          <Trash2 className="h-3 w-3" />
          {t("components.ProductSelectModal.deleteDraft")}
        </button>
      )}
      <DeleteDraftDialog
        open={isConfirmingDelete}
        onOpenChange={handleDeleteDialogOpenChange}
        draftTitle={product.title}
        onConfirm={handleConfirmDeleteDraft}
        isPending={isDeletingDraft}
      />
    </li>
  );
}
